<template>
    <div>
        <div class="loader__wrapper loader__wrapper--loading loader__wrapper--xl" v-if="!searchDetails || !loaded">
            <div class="loader">
                <div class="loader__circle"></div>
                <div class="loader__text">{{labels.loadingJobEffect}}</div>
            </div>
        </div>
        <div class="grid-container" v-if="searchDetails && loaded">
            <div class="grid-x">
                <div class="head head--blue cell">
                    <div class="head__content">
                        <a @click="$router.go(-1)" @keyup.enter="$router.go(-1)" class="head__back" tabindex="0">
                            <span class="icon-arrow-left"></span>
                            {{labels.backToSearchResults}}
                        </a>
                        <h1 class="head__title head__title--detailed">
                            <small>{{labels.targetGroup}}</small>
                            {{ getNames(searchDetails.searchParameters.targetGroup) }}
                            <small>{{labels.intervention}}</small>
                            {{ getNames(searchDetails.searchParameters.activity) }}
                            <span v-if="searchDetails.searchParameters.effectTarget">
                                <small>{{labels.effect}}</small>
                                {{ getNames(searchDetails.searchParameters.effectTarget) }}
                            </span>
                            <span v-if="searchDetails.searchParameters.effectType">
                                <small>{{labels.effectType}}:</small>
                                {{getNames(searchDetails.searchParameters.effectType)}}
                            </span>
                        </h1>
                    </div>
                    <footer class="head__foot">
                        <span class="head__foot-item">
                            <span class="">{{labels.age}}</span> {{getNames(searchDetails.searchParameters.ageGroup) || '-'}}
                        </span>
                        <span class="head__foot-item">
                            <span class="">{{labels.gender}}</span> {{getNames(searchDetails.searchParameters.gender) || '-'}}
                        </span>
                        <span class="head__foot-item">
                            <span class="">{{labels.educationLevel}}</span> {{getNames(searchDetails.searchParameters.educationLevel) || '-'}}
                        </span>
                        <span class="head__foot-item">
                            <span class="">{{labels.socialAspects}}</span> {{getNames(searchDetails.searchParameters.socialCondition) || '-'}}
                        </span>
                         <span class="head__foot-item" v-if="searchDetails.searchParameters.filterGeographicalArea">
                            <span class="">{{labels.geograficalArea}}</span> {{searchDetails.searchParameters.filterGeographicalArea.namePicked || '-'}}
                        </span>
                    </footer>
                </div>
                <div class="content content--gray content--offset cell">

                    <div class="chart grid-x">
                        <div class="medium-6 large-4 cell">
                            <div class="chart__title">
                                {{labels.numberOfStudies}}
                                <popup-info-box
                                    :popoverClasses="'popover--icon'" 
                                    :text="addCountToHtml(this.labels.studyCountMouseOver)" >
                                </popup-info-box>
                            </div>
                            <div class="chart__item">
                                <div class="chart__amount">{{searchDetails.algorithmResult.studyCount}}</div>
                                <div class="doughnut__wrapper">
                                    <doughnut-chart :chartData="studyDoughnut" :chartId="'dc1'"></doughnut-chart>
                                </div>
                            </div>
                        </div>
                        <div class="medium-6 large-4 cell">
                            <div class="chart__title">
                                {{labels.stockOfKnowledge}}
                                <popup-info-box 
                                    :popoverClasses="'popover--icon'" 
                                    :text="this.labels.stockOfKnowledgeMouseOver" >
                                </popup-info-box>
                            </div>
                            <div class="chart__item">
                                <div class="chart__amount">{{ Math.round(searchDetails.algorithmResult.knowledgeQuantityTotal * 100) / 100 }}</div>
                                <div class="doughnut__wrapper">
                                    <doughnut-chart :chartData="knowledgeDoughnut" :chartId="'dc2'"></doughnut-chart>
                                </div>
                            </div>
                        </div>

                        <div class="medium-6 large-4 cell">
                            <div class="chart__title">
                                {{labels.nvm}}
                                 <popup-info-box 
                                    :popoverClasses="'popover--icon'" 
                                    :text="this.labels.calculatedNvmMouseOver" >
                                 </popup-info-box>
                            </div>
                            <div class="chart__item">
                                <div class="chart__amount chart__amount--last">
                                    <span v-if="searchDetails.algorithmResult.jobeffekt < 3">{{ Math.round(searchDetails.algorithmResult.nvmPct)}}%</span>
                                    <small v-else>{{searchDetails.algorithmResult.jobeffektName}}</small>
                                </div>
                                <div class="doughnut__wrapper">
                                    <doughnut-chart :chartData="nvmDoughnut" :chartId="'dc3'"></doughnut-chart>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="chart cell">


                    <div class="effect-summary grid-x">
                        <div class="medium-5 medium-offset-1 cell">
                            <effect-label :value="searchDetails.algorithmResult.jobeffekt" :mega="true" :type="'label'" :name="searchDetails.algorithmResult.jobeffektName"></effect-label>
                        </div>
                        <div class="medium-6 cell">
                            <span class="icon-star icon--effect"
                                v-for="e of [1, 2, 3]"
                                :key="e"
                                :class="{'icon--semi': searchDetails.algorithmResult.evidenceLevel < e }"></span>
                            <span class="effect-summary__label">{{searchDetails.algorithmResult.evidenceLevelName}}</span>
                            
                                <popup-info-box 
                                    :popoverClasses="'popover--icon'" 
                                    :text="this.labels.evidenceLabelMouseOver" >
                                </popup-info-box>

                        </div>
                    </div>
                </div>
                <div class="table__wrapper cell">
                    <table class="table table--effect-details">
                        <caption>
                            <h2 class="table--h2">{{labels.studyList}}</h2>
                        </caption>
                        <thead>
                            <tr>
                                <th>{{labels.author}}</th>
                                <th>{{labels.titleOfStudy}}</th>
                                <th>{{labels.yearOfPublication}}</th>
                                <th>{{labels.effectType}}</th>
                                <th>{{labels.studyConclussionOnEffect}}</th>
                                <th>
                                    {{labels.calculatedKnowledgeContribution}}
                                    
                                <popup-info-box 
                                    :popoverClasses="'popover--icon'" 
                                    :text="this.labels.calculatedKnowledgeContributionMouseOver" >
                                </popup-info-box>

                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(study, k, i) of searchDetails.algorithmResult.studyResultDetails" :key="i" @click="openStudy(study.studyId, study.id)" @keyup.enter="openStudy(study.studyId, study.id)" :title="study.title" tabindex="0">
                                <td class="icon-circle-right">{{study.authors}}</td>
                                <td>{{trimmedTitle(study.studytitle)}}</td>
                                <td>{{study.publicationYear}}</td>
                                <td>{{study.meassuredEffectType}}</td>
                                <td>
                                    <b :class="colors[study.effectConclusion-1].css">
                                        {{study.effectConclusionName}}
                                    </b>
                                </td>
                                <td>{{Math.round(study.knowledgeQuantity * 100) / 100}}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <buttons :hideExport="false" :texts="modalLabels()" :saveStatus="saveStatus" @save="save($event)"></buttons>
            </div>
        </div>
    </div>
</template>

<script>
import { each, orderBy, find, map, uniqBy } from "lodash";
import PopupInfoBox from '../PopupInfoBox.vue';

export default {
  components: { PopupInfoBox },
  name: "SearchDetails",
  data(){
    return {
      evidenceLabels: ["", "Indikation", "Moderat", "Stærk"],
      colors: [
        {
          name: "red",
          color: "#c8102e",
          value: 0,
          css: "text--red text--o"
        },
        {
          name: "yellow",
          color: "#f9f591",
          value: 0,
          css: "text--yellow text--o"
        },
        {
          name: "green",
          color: "#089c01",
          value: 0,
          css: "text--green text--o"
        }
      ],
      pop: "",
      saveStatus: null
    };
  },
  mounted() {
    this.$store.dispatch("getSearchDetails", this.$route.query);
    this.$store.dispatch("getLabels", { label: "pages.jobeffectdetails" });
    each(this.colors, color => (color.value = 0));
  },
  computed: {
    loaded() {
      return this.$store.state.searchDetailsLoaded;
    },
    labels() {
      return this.$store.state.labels["pages.jobeffectdetails"] || {};
    },
    searchDetails() {
      var search = this.$store.state.searchDetails;
      if (search !== null) {
        search.algorithmResult.studyResultDetails = orderBy(
          search.algorithmResult.studyResultDetails,
          ["knowledgeQuantity"],
          ["desc"]
        );
      }
      return search;
    },
    studyDoughnut() {
      var data = [];
      var backgroundColor = [];

      each(this.searchDetails.algorithmResult.studyResultDetails, item => {
        data.push(1);
        backgroundColor.push(this.colors[item.effectConclusion - 1].color);
      });

      return {
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColor.sort()
          }
        ]
      };
    },
    knowledgeDoughnut() {
      var data = [];
      var _vals = [];
      var _colors = [];

      each(this.searchDetails.algorithmResult.studyResultDetails, item => {
        data.push({
          val: item.knowledgeQuantity,
          color: this.colors[item.effectConclusion - 1].color
        });
      });

      data = orderBy(data, ["color"]);
      each(data, item => {
        _vals.push(item.val);
        _colors.push(item.color);
      });

      return {
        datasets: [
          {
            data: _vals,
            backgroundColor: _colors
          }
        ]
      };
    },

    nvmDoughnut(){
        var _effektNr = this.searchDetails.algorithmResult.jobeffekt;
        var _use = _effektNr < 4;
        var colors = ["#CCCCCC"];
        var data = [(100 - this.searchDetails.algorithmResult.nvmPct)];

        if (_use) {
            data.unshift(this.searchDetails.algorithmResult.nvmPct);

            if(_effektNr === 0){
                colors.unshift(find(this.colors, { name: "green" }).color)
            } else if (_effektNr === 1) {
                colors.unshift(find(this.colors, { name: "red" }).color)
            } else if (_effektNr === 2) {
                colors.unshift(find(this.colors, { name: "yellow" }).color)
            }
        } 
        return {
        datasets: [
                {
                data: data,
                backgroundColor: colors
                }
            ]
        };
    }
  },
  methods: {
    openStudy(studyId, resultId) {
      this.$router.push({
        name: "Study",
        params: { id: studyId },
        query: { result: resultId }
      });
    },
    testText(test){
        this.showNow = test;
        console.info(test);
    },
    testPopupShow(index)
    {
        console.info(index);
        console.info(this.$refs);
            this.$refs[index].show();
    },
    trimmedTitle(title) {
      if (title.length > 65) {
        title = title.slice(0, 55) + "...";
      }
      return title;
    },
    save(data) {
        let vals = this.$route.query;
        let activity = vals['activity'];
        let effectTarget = vals['effectTarget'];
        vals['activity'] = (typeof activity === 'string') ? [activity] : activity;
        vals['effectTarget'] = (typeof effectTarget === 'string') ? [effectTarget] : effectTarget;

      this.$store
        .dispatch("save", {
          type: "jobeffect-results",
          name: data.name,
          description: data.msg,
          jobeffectParameters: vals
        })
        .then(() => {
          this.saveStatus = "succes";
        })
        .catch(error => {
          this.saveStatus = "error";
          console.warn(error);
        });
    },
    getNames(arr){
        return map(arr, 'namePicked').join(', ');
    },
    modalLabels() {
      return {
        saveDialog: {
          name: this.labels.saveInMyLibraryName,
          title: this.labels.saveInMyLibraryTitle,
          text: this.labels.saveInMyLibraryText,
          succes: this.labels.saveInMyLibrarySucces,
          error: this.labels.saveInMyLibraryError,
          button: this.labels.saveInMyLibraryButton,
          note: this.labels.saveInMyLibraryNote
        },
        emailDialog: {
          button: this.labels.tipEmailButton,
          message: this.labels.tipEmailMessage,
          receiverEmail: this.labels.tipEmailRecieverEmail,
          receiverName: this.labels.tipEmailRecieverName,
          senderEmail: this.labels.tipEmailSenderEmail,
          senderName: this.labels.tipEmailSenderName,
          success: this.labels.tipEmailSuccess,
          text: this.labels.tipEmailText,
          title: this.labels.tipEmailTitle
        }
      };
    },
    addCountToHtml(html){
        let _uniq = uniqBy(this.searchDetails.algorithmResult.studyResultDetails, 'studyId');
        return html.replace('{studyCount}', _uniq.length);
    }

  }
};
</script>



// WEBPACK FOOTER //
// src/components/jobeffects-search/SearchDetails.vue